import React, { useEffect, useState, useRef } from 'react';
import PersonalInformation from '../components/onboarding/PersonalInformation';
import BusinessDetails from '../components/onboarding/BusinessDetails';
import OperationDetails from '../components/onboarding/OperationDetails';
import MSMERegistration from '../components/onboarding/MSMERegistration';
import BankDetails from '../components/onboarding/BankDetails';
import OnboardingSuccess from '../components/onboarding/OnboardingSuccess';
import StepNavigator from '../components/onboarding/StepNavigator';
import { getDecryptedUrl, useIsMobile } from 'lib/utils';
import { useHistory } from 'react-router-dom';
import Stepper from '../components/onboarding/Stepper';
import InstructionsModal from '../components/onboarding/InstructionsModal';
import useOnboarding from '../hooks/useOnboarding';
import { Row, Col, Container, Button } from 'react-bootstrap';

import '../components/onboarding/Stepper.css';
import '../components/onboarding/Onboarding.css'
import notifications from 'lib/notifications';
import inMemoryJWT from 'services/auth/inMemoryJWT';
import storage from 'lib/storage';
import config from 'config';


const steps = [
  {
    title: 'Personal Information',
    description: 'Tell us about yourself',
  },
  {
    title: 'Business Details',
    description: 'Complete your business profile',
  },
  {
    title: 'Address Details',
    description: 'Complete your address details',
  },
  {
    title: 'Udyog Aadhaar Registration',
    description: 'Verify your Udyog Aadhaar registration',
  },
  {
    title: 'Bank Details',
    description: 'Verify your bank details',
  },
];

const OnboardingWizard = ({ user }) => {
  const history = useHistory();
  const urlParams = new URLSearchParams(window.location.search);
  const isFromMobile = urlParams.get('isWebview') === 'true';
  const {
    currentStep,
    totalSteps,
    sessionData,
    userEmail,
    userPhone,
    isLoading,
    error,
    setError,
    nextStep,
    prevStep,
    setUserData
  } = useOnboarding();
  const isMobile = useIsMobile();

  // State to store current step data
  const [currentStepData, setCurrentStepData] = useState({});
  const [allStepData, setAllStepData] = useState({});
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null); // 'success', 'error', null
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [registrationMessage, setRegistrationMessage] = useState('Hello');
  const [isSaveButtonDisable, setIsSaveButtonDisable] = useState(false);
  const [isNextButtonDisable, setIsNextButtonDisable] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const loc = getDecryptedUrl(window.location.href);
  const isBusinessKYC = loc.includes('isBusinessKYC=true');
  const isCreateAccount = loc.includes('isCreateAccount=true');

  // When an error occurs, ensure the page scrolls to the top so the message is visible
  useEffect(() => {
    if (error) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    }
  }, [error]);

  // useEffect(() => {
  //   const isDevelopmentMode = true; // Change to false when done with development

  //   if (isDevelopmentMode) {
  //     setShowSuccessMessage(true);
  //     setRegistrationStatus('success');
  //     setRegistrationMessage('Your account has been created and verified. You will be redirected shortly...');
  //   }
  // }, []);



  // Refs for all step components
  const personalInfoRef = useRef();
  const businessDetailsRef = useRef();
  const operationDetailsRef = useRef();
  const msmeRegistrationRef = useRef();
  const bankDetailsRef = useRef();


  useEffect(() => {
    console.log('OnboardingWizard mounted', {
      user,
      path: window.location.pathname,
      isAuthenticated: user?.isAuthenticated
    });
  }, [user]);

  // Scroll to top whenever step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleStepDataChange = (data) => {
    setCurrentStepData(data);
    console.log(`Step ${currentStep} data:`, data);
  };

  // Custom next handler for StepNavigator
  const handleNext = () => {
    console.log('=== NEXT BUTTON CLICKED ===');
    console.log('Current step:', currentStep);
    console.log('Current step data:', currentStepData);
    console.log('All collected data so far:', allStepData);

    // Trigger form submission based on current step
    let currentRef = null;
    switch (currentStep) {
      case 1:
        currentRef = personalInfoRef.current;
        break;
      case 2:
        currentRef = businessDetailsRef.current;
        break;
      case 3:
        currentRef = operationDetailsRef.current;
        break;
      case 4:
        currentRef = msmeRegistrationRef.current;
        break;
      case 5:
        currentRef = bankDetailsRef.current;
        break;
    }

    if (currentRef && currentRef.submitForm) {
      console.log('Triggering form submission for step', currentStep);
      currentRef.submitForm();
    } else {
      console.log('No form to submit, proceeding to next step');
      nextStep();
    }
  };

  const handleStepComplete = async (stepData) => {
    console.log(`=== STEP ${currentStep} COMPLETED ===`);
    console.log('Step data received:', stepData);
    console.log('Step data type:', typeof stepData);
    console.log('Step data keys:', Object.keys(stepData));

    // Store the step data
    const newAllStepData = { ...allStepData };
    switch (currentStep) {
      case 1:
        newAllStepData.personalInformation = stepData.personalInformation;
        break;
      case 2:
        newAllStepData.businessDetails = stepData.businessDetails;
        break;
      case 3:
        newAllStepData.operationDetails = stepData.operationDetails;
        break;
      case 4:
        newAllStepData.msmeRegistration = stepData.msmeRegistration;
        break;
      case 5:
        newAllStepData.bankDetails = stepData.bankDetails;
        break;
    }

    setAllStepData(newAllStepData);
    console.log('Updated all step data:', newAllStepData);

    // If this is the last step, handle completion
    if (currentStep === 5) {
      console.log('=== FINAL ONBOARDING DATA ===');
      console.log('Complete onboarding data:', newAllStepData);
      console.log('Session data:', sessionData);

      // The registration result will be handled by the nextStep function
      // We'll check the result there and show appropriate feedback
    }

    // Pass the complete step data to nextStep to maintain proper structure
    console.log('Calling nextStep with stepData:', stepData);
    const result = await nextStep(stepData);
    console.log('Next step result:', result);

    // Check if this was the final step and registration was successful
    if (currentStep === 5 && result && result.success) {
      // Show success message
      notifications.show({
        type: 'success',
        message: 'Onboarding completed successfully',
        key: 'onboarding-success',
      });
      if (isFromMobile) {
        try {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({ type: 'KYC_COMPLETE' })
          );
        } catch (e) {
          console.error('postMessage failed', e);
        }
        const u = new URL(window.location.href);
        u.pathname = '/onboarding';
        u.searchParams.set('isWebview', 'true');
        u.searchParams.set('kyc-complete', 'true');
        window.location.replace(u.toString());
        return;
      }

      setShowSuccessMessage(true);
      setRegistrationStatus('success');
      setRegistrationMessage(result.registrationData?.message || 'Your account has been created and verified.');
      if(isCreateAccount){
        history.push(`/kycapproval/${isBusinessKYC ? 'business' : 'individual'}?page=1`)
      }else{
        console.log('KYCOnboardResult', result)

        const token = result?.registrationData?.login_response?.jwt_token;
        const tokenExpiry = result?.registrationData?.login_response?.jwt_token_expire_datetime;

        const rfToken = result?.registrationData?.login_response?.refresh_token;
        await inMemoryJWT.setToken(token, tokenExpiry);

        await inMemoryJWT.setRefreshToken(rfToken);

        //TODO : tmp - storing person_id in local storage cause refreshToken api needs it
        const pid = result?.registrationData?.login_response?.person_id;
        await storage.setItem('pid', `${pid}`);

        //also storing sid since we need to call this after calling refresh token
        const sid = result?.registrationData?.login_response?.social_profile_id;
        await storage.setItem('sid', `${sid}`);

        // now call the api to upload the cheque, the image is available in localstorage in base64.. convert it back to image and upload it and use companyId
        try {
          const companyId = localStorage.getItem('onboardedCompanyId');
          const savedCheque = localStorage.getItem('cancelledChequeImage');
          if (companyId && savedCheque) {
            const payload = JSON.parse(savedCheque);
            const dataUrl = payload?.dataUrl;
            const name = payload?.name || 'cheque.jpg';
            const mimeMatch = (dataUrl || '').match(/^data:(.*?);base64,/);
            const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
            const base64 = (dataUrl || '').split(',')[1] || '';
            const binary = atob(base64);
            const len = binary.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
            const blob = new Blob([bytes], { type: mime });

            const entity_type_enum = 15;
            const front_back_enum = 1;
            const attachmentIdToPass = 0;
            const document_module_enum = (allStepData?.businessDetails?.typeOfIndividual == 2 || sessionData?.businessDetails?.typeOfIndividual == 2) ? 1 : 2;
            const company_affiliate_enum = 1;

            const formData = new FormData();
            formData.append('entity_Id', String(companyId));
            formData.append('entity_Type_Enum', String(entity_type_enum));
            formData.append('document_Module_Enum', String(document_module_enum));
            formData.append('company_Affiliate_Enum', String(company_affiliate_enum));
            formData.append('user_Type_Enum', '2');
            formData.append('front_Back_Enum', String(front_back_enum));
            formData.append('attachment_File', blob, name);
            formData.append('attachment_Id', String(attachmentIdToPass));

            const url = `${config.baseUrl}/BioFuelCommonUtilities/api/DocumentUpload/AttachDocument_Azure`;
            const authToken = globalThis.accessToken || localStorage.getItem('accessToken') || '';
            console.log('Uploading cancelled cheque to:', url, 'entity_Id:', companyId);
            const uploadResp = await fetch(url, {
              method: 'POST',
              headers: {
                Authorization: authToken,
              },
              body: formData,
            });
            console.log('Cancelled cheque upload response status:', uploadResp.status);
            if (uploadResp.ok) {
              localStorage.removeItem('cancelledChequeImage');
            }
            localStorage.removeItem('cancelledChequeImage');
          }
        } catch (e) {
          console.log('Cancelled cheque upload skipped/failed:', e?.message || e);
        }


        // now call the api to upload the cheque, the image is available in localstorage in base64.. convert it back to image and upload it and use companyId

        setTimeout(async()=>{
            history.push('/social/feed');
            setTimeout(()=>{
              window.location.reload();
            },1000)
        }, 1000)
      }

      // // You can add redirect logic here or show a success modal
      // setTimeout(() => {
      //   // Redirect to dashboard or show success page
      //   console.log('Onboarding completed successfully!');
      //   // window.location.href = '/dashboard'; // Uncomment to redirect
      // }, 3000);
    } else if (currentStep === 5 && result && !result.success) {
      // Registration failed
      setRegistrationStatus('error');
      // Error message is already set in the hook
    }
  };

  const renderStep = () => {
    console.log('Rendering step:', currentStep);
    let title = steps[currentStep - 1]?.title;

    switch (currentStep) {
      case 1:
        return (
          <PersonalInformation
            ref={personalInfoRef}
            title={title}
            setError={setError}
            onComplete={(stepData) => {
              const { personalInformation } = stepData;
              if (personalInformation) {
                setUserData(personalInformation.email, personalInformation.phone);
              }
              handleStepComplete(stepData);
            }}
            isNextButtonDisable = {isNextButtonDisable}
            setIsNextButtonDisable = {setIsNextButtonDisable}
          />
        );
      case 2:
        return (
          <BusinessDetails
            ref={businessDetailsRef}
            title={title}
            onComplete={handleStepComplete}
            isNextButtonDisable = {isNextButtonDisable}
            setIsNextButtonDisable = {setIsNextButtonDisable}
          />
        );
      case 3:
        return (
          <OperationDetails
            ref={operationDetailsRef}
            title={title}
            onComplete={handleStepComplete}
            validatedPincode={allStepData?.businessDetails?.validatedPincode}
            fetchedRegisteredAddress={allStepData?.businessDetails?.fetchedRegisteredAddress}
            fetchedAddressSource={allStepData?.businessDetails?.fetchedAddressSource}
            authorizedSignatories={allStepData?.businessDetails?.authorizedSignatories}
            companyName={allStepData?.businessDetails?.companyName}
            isNextButtonDisable = {isNextButtonDisable}
            setIsNextButtonDisable = {setIsNextButtonDisable}
          />
        );
      case 4:
        return (
          <MSMERegistration
            ref={msmeRegistrationRef}
            title={title}
            onComplete={handleStepComplete}
          />
        );
      case 5:
        return (
          <BankDetails
            ref={bankDetailsRef}
            title={title}
            onComplete={handleStepComplete}
            buyerSellerType={allStepData?.businessDetails?.buyerSellerTypeEnumName}
            isSaveButtonDisable = {isSaveButtonDisable}
            setIsSaveButtonDisable = {setIsSaveButtonDisable}
          />
        );
      default:
        console.log('No step matched for:', currentStep);
        return null;
    }
  };

  return (
    <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <div className="bg-white rounded shadow p-4 ow-boarding-container" style={{ maxWidth: 1200, width: isMobile? '95vh' : '80vw', marginTop: isMobile ? '-40px' : '0px' }}>


        {/* {showSuccessMessage && registrationStatus === 'success' && (
          <OnboardingSuccess
            registrationMessage={registrationMessage}
            onContinue={() => {
              setShowSuccessMessage(false);
              setRegistrationStatus(null);
              window.location.href = '/';
            }}
          />
        )} */}

        {/* Error banner beside Instructions Button */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: error ? 'space-between' : 'flex-end', marginBottom: '1rem', gap: '12px', flexWrap: 'wrap' }}>
          {error && (
            <div role="alert" style={{
              background: '#fde2e1',
              border: '1px solid #f5c2c7',
              color: '#842029',
              padding: '8px 12px',
              borderRadius: 6,
              flex: '1 1 auto',
              minWidth: 260
            }} className="d-flex align-items-center justify-content-between">
              <div style={{ fontWeight: 500, paddingRight: 8 }}>
                Registration failed — your details are saved, but we couldn't create your account right now.
              </div>
              <button
                type="button"
                onClick={() => setShowErrorDetails((v) => !v)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#842029',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  marginLeft: 12,
                  whiteSpace: 'nowrap'
                }}
              >
                {showErrorDetails ? 'Hide details' : 'Show details'}
              </button>
            </div>
          )}
          <button
            onClick={() => setIsInstructionsModalOpen(true)}
            style={{
              padding: '8px 16px',
              borderRadius: '18px',
              border: '1px solid #17a2b8',
              background: '#fff',
              color: '#17a2b8',
              cursor: 'pointer',
              fontWeight: 500,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#17a2b8';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#17a2b8';
            }}
          >
            View Requirements
          </button>
        </div>
        {showErrorDetails && error && (
          <div className="mt-2 p-2 bg-light border rounded" style={{ maxHeight: 200, overflow: 'auto' }}>
            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{String(error)}</pre>
          </div>
        )}

        {!showSuccessMessage && (
          <>
            {isMobile && (
              <div className="sticky-top bg-white py-2" style={{ zIndex: 1000, borderBottom: '1px solid #eee' }}>
                <Stepper currentStep={currentStep} steps={steps} />
              </div>
            )}
            <Row className="align-items-center" style={{ minHeight: '600px' }}>
              {!isMobile && <Col md={4} className="d-flex justify-content-center" style={{ minHeight: !isMobile && '400px' }}>
                <div className="w-100 d-flex flex-column align-items-center justify-center" style={{ minHeight: !isMobile && '400px' }}>
                  <Stepper currentStep={currentStep} steps={steps} />
                </div>
              </Col>}
              <Col md={7} className="d-flex justify-content-center align-items-center">
                <div className="w-100" style={{ maxWidth: 550 }}>
                  {renderStep()}
                </div>
              </Col>
            </Row>
            <div className="mt-4">
              <StepNavigator
                currentStep={currentStep}
                totalSteps={totalSteps}
                prevStep={prevStep}
                nextStep={handleNext}
                isLoading={isLoading}
                stepData={currentStepData}
                isSaveButtonDisable = {isSaveButtonDisable}
                isNextButtonDisable = {isNextButtonDisable}
                setIsNextButtonDisable = {setIsNextButtonDisable}
              />
            </div>
          </>
        )}

        {/* Instructions Modal */}
        <InstructionsModal
          isOpen={isInstructionsModalOpen}
          toggle={() => setIsInstructionsModalOpen(false)}
        />
      </div>
    </Container>
  );
};

export default OnboardingWizard;
